import React, {useEffect, useRef, memo, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import {LazyImage} from 'react-lazy-images'
import {Helmet} from 'react-helmet'

/* Utils */
import {
  generateImageSrc,
  generateImageSrcSet,
  isAvailableRetinaImage,
  generateImageWebp
} from '../imageUtils'

/* Styles */
import s from './hb-image-view.scss'

const HbImageView = memo((props) => {
  const {
    alwaysRetina,
    image,
    shouldLoadEagerly,
    loadAnimation,
    retinaSupport,
    webpSupport,
    withPlaceholder,
    isBot,
    forcePreload,
    className,
    onClick,
  } = props

  const imageRef = useRef(null);

  const handleClickWrapperImage = useCallback(() => {
    onClick?.()
  },[onClick])

  useEffect(() => {
    if (imageRef && imageRef.current && shouldLoadEagerly) {
      let errorLoadingImgBeforeHydration =
        imageRef?.current?.complete && imageRef?.current?.naturalHeight === 0;

      if (errorLoadingImgBeforeHydration) {
        imageRef?.current?.parentNode?.classList?.add(s.errorImage);
        imageRef?.current?.classList?.add(s.hideImage);
      }
    }
  }, [imageRef.current?.complete])


  const handleOnError = useCallback((e) => {
    e.target.parentNode.classList.add(s.errorImage);
    e.target.classList.add(s.hideImage);
  },[])

  const {src, isOriginalImage} = useMemo(() => generateImageSrc(image), [image]);

  const isRetinaAvailable = useMemo(() => retinaSupport && image.originSize && image.componentSize
    ? isAvailableRetinaImage(image?.originSize, image?.componentSize)
    : Boolean(alwaysRetina),
  [alwaysRetina, image?.componentSize, image?.originSize, retinaSupport])


  const srcSet = useMemo(() => !isOriginalImage && isRetinaAvailable && generateImageSrcSet(src, image?.size, image?.src), [isOriginalImage, isRetinaAvailable, src, image?.size, image?.src]);

  const srcSetString = useMemo(() => !isOriginalImage && isRetinaAvailable && `${srcSet?.png} 1x, ${srcSet?.png2x} 2x`, [isOriginalImage, isRetinaAvailable, srcSet])

  const webpString = useMemo(() => {
    const webp = !isOriginalImage && webpSupport && generateImageWebp(srcSet || src)

    return !isOriginalImage && webpSupport &&
      (isRetinaAvailable
        ? `${webp?.webp} 1x, ${webp?.webp2x} 2x`
        : `${webp?.webp} 1x`
      )
  }, [isOriginalImage, webpSupport, srcSet, src, isRetinaAvailable])


  if (isBot) {
    return <img alt={image.alt} src={src}/>;
  }

  return (
    <div className={cx(s.root, s[className], className, 'hb-image-view')} onClick={handleClickWrapperImage}>
      {forcePreload && (
        <Helmet>
          <link rel='preload' fetchpriority='high' as='image' imagesrcset={webpString} href={src}/>
        </Helmet>
      )}
      <LazyImage
        loadEagerly={shouldLoadEagerly}
        alt={image.alt}
        title={image.title}
        placeholder={({imageProps, ref}) => (
          <div
            className={cx(s.lazyImagePlaceholder, 'hb-image-view__placeholder')}
            ref={ref}></div>
        )}
        actual={({imageProps, ref}) => (
          <picture>
            {webpString && (
              <source
                className={cx(s.image, 'hb-HbImage-view__image')}
                type='image/webp'
                srcSet={webpString}/>
            )}
            {srcSetString && (
              <source
                className={cx(s.image, 'hb-HbImage-view__image')}
                srcSet={srcSetString}/>
            )}
            <img
              {...imageProps}
              ref={imageRef}
              onError={handleOnError}
              itemProp={image.itemProp || null}
              className={cx(s.image, 'hb-HbImage-view__image')}
              src={src}/>
          </picture>
        )}/>

      <noscript>
        <img alt={image.alt} src={src}/>
      </noscript>
    </div>
  );
});

HbImageView.defaultProps = {
  loadAnimation: true,
  retinaSupport: true,
  webpSupport: true,
  withPlaceholder: true,
  forcePreload: false,
}

HbImageView.propTypes = {
  className: PropTypes.string,
  image: PropTypes.shape({
    linkFormat: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    size: PropTypes.string,
    src: PropTypes.string,
    componentSize: PropTypes.object,
    originSize: PropTypes.object,
    itemProp: PropTypes.string,
  }),
  shouldLoadEagerly: PropTypes.bool,
  loadAnimation: PropTypes.bool,
  retinaSupport: PropTypes.bool,
  webpSupport: PropTypes.bool,
  alwaysRetina: PropTypes.bool,
  imageType: PropTypes.string,
  withPlaceholder: PropTypes.bool,
  onClick: PropTypes.func,
  forcePreload: PropTypes.bool,
  imageLoadErrorComponent: PropTypes.element
}

export default HbImageView
